import {
  ChangeDetectionStrategy, Component, computed, inject,
} from '@angular/core';
import { Router } from '@angular/router';
import { PaperButtonComponent, PaperIconComponent } from '@ricoh-paper/angular';
import { TranslocoPipe } from '@jsverse/transloco';
import { AiReturnService } from '../../../../core/services/ai-return.service';
import { ShellStateService } from '../../../../core/services/shell-state.service';
import { AnalyticsService } from '../../../../core/services/analytics.service';
import { ANALYTICS_EVENTS } from '../../../../core/analytics/events';
import { Z_INDEX } from '../../../../core/constants/z-index';

/**
 * im-ai-return-chip — floating "Back to chat" pill on the map.
 *
 * Appears after the full-screen Workplace AI chat jumps the user to the
 * map ("view on map"), so the conversation is one tap away. Self-gating:
 * renders nothing unless the return offer is armed, the user is on the
 * map, and the AI side panel isn't already showing the conversation.
 * Mounted once at shell level (app-shell template).
 */
@Component({
  selector: 'im-ai-return-chip',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PaperButtonComponent, PaperIconComponent, TranslocoPipe],
  template: `
    @if (visible()) {
      <div class="ai-return-chip" [style.z-index]="zIndex">
        <button
          type="button"
          class="ai-return-chip__main ph-no-capture"
          (click)="returnToChat()">
          <paper-icon name="ask-mark" [size]="14" />
          <span>{{ 'shell.ai_panel.back_to_chat' | transloco }}</span>
        </button>
        <paper-button
          class="ai-return-chip__dismiss ph-no-capture"
          intent="ghost"
          shape="icon"
          size="sm"
          icon="close"
          [iconSize]="12"
          [ariaLabel]="'common.dismiss' | transloco"
          [title]="'common.dismiss' | transloco"
          (click)="dismiss()" />
      </div>
    }
  `,
  styles: [`
    .ai-return-chip {
      position: fixed;
      left: 50%;
      transform: translateX(-50%);
      /* Clears the floating bottom nav pill (fixed at --s-5 + safe area,
         ~44px tall) with one spacing step of air between the two. */
      bottom: calc(var(--s-5, 24px) + env(safe-area-inset-bottom, 0px) + 52px);
      display: inline-flex;
      align-items: center;
      background: var(--card-bg);
      backdrop-filter: var(--card-blur);
      -webkit-backdrop-filter: var(--card-blur);
      border: 1px solid var(--border-low);
      border-radius: var(--radius-pill, 100px);
      box-shadow: var(--panel-shadow, var(--shadow-md));
      padding: 3px;
      animation: ai-return-chip-in 0.3s var(--ease, cubic-bezier(0.16, 1, 0.3, 1)) both;
    }

    .ai-return-chip__main {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 6px 12px;
      background: none;
      border: none;
      border-radius: var(--radius-pill, 100px);
      cursor: pointer;
      color: var(--text);
      font-size: 12px;
      font-weight: 500;
      font-family: inherit;
      white-space: nowrap;
      transition: background var(--motion-fast, 150ms) var(--ease, ease);

      paper-icon { color: var(--text-secondary); } /* an icon is never the accent (One Paper ruling 15d) */

      &:hover { background: var(--hover-overlay); }

      &:focus-visible {
        outline: 2px solid var(--brand-focus-ring, var(--color-primary));
        outline-offset: 2px;
      }
    }

    .ai-return-chip__dismiss {
      margin-right: 2px;
    }

    @keyframes ai-return-chip-in {
      from {
        opacity: 0;
        transform: translateX(-50%) translateY(8px);
      }
      to {
        opacity: 1;
        transform: translateX(-50%) translateY(0);
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .ai-return-chip { animation: none; }
      .ai-return-chip__main { transition: none; }
    }
  `],
})
export class AiReturnChipComponent {
  private readonly router = inject(Router);
  private readonly analytics = inject(AnalyticsService);
  protected readonly aiReturn = inject(AiReturnService);
  protected readonly shell = inject(ShellStateService);

  protected readonly zIndex = Z_INDEX.NAV;

  /** Only on the map, and only while the side panel isn't already
   *  showing the same conversation. */
  protected readonly visible = computed(() =>
    this.aiReturn.active()
    && this.shell.currentSection() === 'map'
    && this.shell.rightPanel() !== 'ai');

  returnToChat(): void {
    this.analytics.capture(ANALYTICS_EVENTS.AI_RETURN_TO_CHAT_CLICKED, {
      ms_since_armed: this.aiReturn.msSinceArmed(),
    });
    // The service self-dismisses when navigation lands on /ai.
    this.router.navigate(['/ai']);
  }

  dismiss(): void {
    this.analytics.capture(ANALYTICS_EVENTS.AI_RETURN_TO_CHAT_DISMISSED, {
      ms_since_armed: this.aiReturn.msSinceArmed(),
    });
    this.aiReturn.dismiss();
  }
}
